import * as React from "react";
import { Button, Popconfirm, Switch, Tag, Tooltip } from "antd";
import Table, { ColumnProps } from "antd/lib/table";
import TaskStatusesVisualStore from "../stores/TaskStatusesVisualStore";
import { TaskStatus } from "../types";

const getStatusBadgeLabels = (status: TaskStatus) => {
    const labels: Array<{ text: string; color: string }> = [];

    if (status.isDefault) {
        labels.push({ text: "Initial", color: "#EAF7EA" });
    }

    if (status.isFinal) {
        labels.push({ text: "Final", color: "#EAF7EA" });
    }

    return labels;
};

const getStatusActionsLabel = (status: TaskStatus) => {
    const count = status.allowedActions?.length ?? 0;

    if (count === 0) {
        return "No actions";
    }

    return `${count} action${count === 1 ? "" : "s"}`;
};

const getDeleteDisabledReason = (status: TaskStatus) => {
    if (status.isDefault && status.isFinal) {
        return "This status cannot be deleted because it is both the default and final status.";
    }

    if (status.isDefault) {
        return "This status cannot be deleted because it is the default status.";
    }

    if (status.isFinal) {
        return "This status cannot be deleted because it is the final status.";
    }

    return undefined;
};

const getNextStatusesLabel = (
    status: TaskStatus,
    taskStatuses: TaskStatus[],
) => {
    const nextStatuses =
        status.transitions
            ?.map((transition) =>
                taskStatuses.find(
                    (candidate) => candidate.id === transition.targetStatusId,
                ),
            )
            .filter(
                (candidate): candidate is TaskStatus =>
                    !!candidate &&
                    candidate.isSubtaskStatus === status.isSubtaskStatus,
            )
            .map((candidate) => candidate.name) ?? [];

    return nextStatuses.length > 0 ? nextStatuses.join(", ") : "None";
};

export const createStandardModeColumns = (
    taskStatuses: TaskStatus[],
): ColumnProps<TaskStatus>[] => [
    {
        title: "Status",
        dataIndex: "name",
        key: "name",
        render: (name: string, record: TaskStatus) => (
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    flexWrap: "wrap",
                }}
            >
                <span>{name}</span>
                {getStatusBadgeLabels(record).map((badge) => (
                    <Tag
                        key={badge.text}
                        style={{
                            marginInlineEnd: 0,
                            borderRadius: 999,
                            border: "none",
                            background: badge.color,
                            color: "#21406D",
                            fontWeight: 600,
                            paddingInline: 8,
                        }}
                    >
                        {badge.text}
                    </Tag>
                ))}
            </div>
        ),
        width: "32%",
    },
    {
        title: "Code",
        dataIndex: "code",
        key: "code",
        width: "20%",
    },
    {
        title: "Assigned actions",
        key: "actionsCount",
        render: (_: string, record: TaskStatus) => (
            <Tag
                style={{
                    marginInlineEnd: 0,
                    borderRadius: 999,
                    border: "none",
                    background: "#EEF1F5",
                    color: "#3B5174",
                    fontWeight: 600,
                    paddingInline: 8,
                }}
            >
                {getStatusActionsLabel(record)}
            </Tag>
        ),
        width: "18%",
    },
    {
        title: "Next statuses",
        key: "nextStatuses",
        render: (_: string, record: TaskStatus) =>
            getNextStatusesLabel(record, taskStatuses),
        width: "30%",
    },
];

export const createCustomModeColumns = (
    store: TaskStatusesVisualStore,
    taskStatuses: TaskStatus[],
): ColumnProps<TaskStatus>[] => [
    ...createStandardModeColumns(taskStatuses),
    {
        title: "Actions",
        width: 200,
        render: (_: string, record: TaskStatus) => {
            const deleteDisabledReason = getDeleteDisabledReason(record);
            const deleteButton = (
                <Button
                    data-id-cells="delete status"
                    title="Delete"
                    size="small"
                    type="link"
                    disabled={!!deleteDisabledReason}
                >
                    <Tooltip
                        title={deleteDisabledReason ? null : "Delete status"}
                        placement="bottom"
                    >
                        <i className="alpha-icon md table-action-delete" />
                    </Tooltip>
                </Button>
            );

            return (
                <div className="row-actions-wrapper">
                    <Button
                        data-id-cells="edit status"
                        title="Edit"
                        size="small"
                        type="link"
                        onClick={() => {
                            store.selectStatus(record);
                        }}
                    >
                        <Tooltip title="Edit status" placement="bottom">
                            <i className="alpha-icon md table-action-edit" />
                        </Tooltip>
                    </Button>
                    {deleteDisabledReason ? (
                        <Tooltip
                            title={deleteDisabledReason}
                            placement="bottom"
                        >
                            <span>{deleteButton}</span>
                        </Tooltip>
                    ) : (
                        <Popconfirm
                            id={`data-id-popconfirm-box-${record.name}`}
                            title="Are you sure you want to delete this status?"
                            onConfirm={() => {
                                store.deleteTaskStatus(record.id);
                            }}
                            placement="topRight"
                        >
                            {deleteButton}
                        </Popconfirm>
                    )}
                    <Switch
                        data-id-cells="is-active"
                        data-id-value={record.isEnabled}
                        style={{
                            visibility: "visible",
                            marginBottom: 18,
                            marginLeft: 14,
                        }}
                        checked={record.isEnabled}
                        onChange={() => {
                            store.toggleTaskStatus(record.id);
                        }}
                    />
                </div>
            );
        },
    },
];

export const renderStatusesTable = (
    columns: ColumnProps<TaskStatus>[],
    statuses: TaskStatus[],
    loading: boolean,
) => (
    <Table
        data-id="table-List-list"
        className="alpha-table statuses"
        loading={loading}
        columns={columns}
        dataSource={statuses}
        rowKey={(status) => status.id}
        pagination={false}
    />
);
